import {isFillableCellElement, type FillableCellElement} from '../lib/cell-fill';
import type {CellElement, TableCellStyle} from '../lib/table-types';
import {cellItemStyle} from '../lib/table-ops';
import {CellElementFill} from './TableCellFill';
import {CellElementView} from './TableCellRenderers';

/**
 * How a table cell's contents are DRAWN — the guide grid around it, the row each
 * element occupies in its stack, and the element itself.
 *
 * Lifted out of `TableElement`, which had reached 1051 lines holding the whole
 * editor. These answer one question — *what is on the page, and why is it what
 * the report will print?* — and none of them touches the selection, drag or
 * keyboard state that makes up the rest of that component, so they read better
 * next to each other than buried at the top of it.
 *
 * `CellStackItem` is the one piece here that both surfaces use: the report draws
 * a cell's stack too, and the wrapper is where the cell's alignment reaches the
 * element. The other two are the editor's alone.
 */

/**
 * The editor's cell grid: a guide for the AUTHOR, drawn on sides they have not
 * ruled themselves. The finished report draws none of it — see the read-only
 * branch below, and `TableElement.reportBorders.test`.
 *
 * **Indigo, because grey was mistaken for ink.** At `border-gray-300` this sat
 * a hair off a real 1px hairline, so an author looking at the last row of a JAC
 * could not tell the guide from a border they had applied — and the line they
 * were sure they had drawn was missing from the printed document. Indigo is the
 * editor's own chrome colour (the selection outlines are indigo-400/500, a step
 * up from this), so the grid now reads as part of the tool rather than part of
 * the page. Colour is not the only cue: dotted-vs-solid still distinguishes it
 * for anyone who cannot use the hue.
 *
 * **`border-dotted` is load-bearing — do not "tidy" it to dashed or solid.**
 * Under `border-collapse` the winning border is decided by STYLE first
 * (double > solid > dashed > dotted), so dotted is what makes any border the
 * author applies win the tie on every side. Equal-width solid-vs-solid ties
 * resolve by position instead — the top/left neighbour wins — which is what
 * used to hide applied top and left borders.
 */
export const CELL_SCAFFOLDING = 'border-dotted border-indigo-300';

/**
 * One cell element as the AUTHOR sees it — which is the control a crew will
 * actually be handed, drawn inert.
 *
 * **The design canvas draws the printed document, not a stand-in for it.** The
 * editor used to render its own static placeholders for the five fillable types
 * (a grey italic box for a text answer, "Image input" for a photo, "Signature"
 * for a pad) while the report drew the real controls. Two implementations of one
 * element is how they drifted — a different height, a different empty-state
 * word, two lines beneath a signature on one screen and none on the other — and
 * every one of those differences reached the author as a document that did not
 * look like the one that printed. So this hands the cell to `CellElementFill`,
 * the same component the report uses, with no fill wiring: `resolveFillMode`
 * reads that as `preview`, which is the appearance a crew meets on an open
 * report.
 *
 * `pointer-events-none` is what keeps it a picture. The control is real enough
 * to have a focus ring and a caret, and the author is not filling anything in —
 * every press has to reach the `<td>` and the element wrapper above it, which
 * own selection, range-drag and double-click-to-edit. The resize corner is a
 * sibling, not a child, so it stays live.
 *
 * A header row and the two decorative types keep the static view, because that
 * is what the REPORT draws for them too (`collectCellFillSlots` skips header
 * rows; `isFillableCellElement` excludes heading and logo). Matching means
 * matching what each cell actually is, not making every cell look answerable.
 *
 * A drag-resize in progress folds its live size into the config rather than
 * riding a separate `overrideH`/`overrideW` channel — `cellElementToRequirement`
 * spreads the config, so the box follows the pointer through the same path the
 * saved size takes.
 */
/**
 * The element as the pointer currently has it, mid drag-resize.
 *
 * Only the two variants that offer a resize corner can be dragged
 * (`canDragResize`), and both carry `width`/`height` in their config — so
 * narrowing on the discriminant rebuilds the config with no cast. It used to
 * spread the union and re-assert it with `as typeof element`, which is the one
 * kind of claim the compiler cannot check: a variant whose config loses
 * `height` would still have compiled.
 *
 * Folded into the config rather than riding a separate `overrideH`/`overrideW`
 * channel, because `cellElementToRequirement` spreads the config — so the box
 * follows the pointer through the same path the saved size takes.
 */
function withDragSize(
  el: FillableCellElement,
  height: number | undefined,
  width: number | undefined,
): FillableCellElement {
  if (height === undefined && width === undefined) return el;
  const size = {
    ...(height === undefined ? {} : {height}),
    ...(width === undefined ? {} : {width}),
  };
  switch (el.type) {
    case 'photo':
      return {...el, config: {...el.config, ...size}};
    case 'signature':
      return {...el, config: {...el.config, ...size}};
    default:
      // Nothing else draws a resize corner, so nothing else is ever handed one.
      return el;
  }
}

export function EditorCellElement({
  element,
  cellKey,
  isHeaderRow,
  cellStyle,
  sampleData,
  textStyle,
  overrideH,
  overrideW,
}: {
  element: CellElement;
  cellKey: string;
  isHeaderRow: boolean;
  /** The cell's own style, for the typography its control renders text in. */
  cellStyle?: TableCellStyle;
  sampleData: Record<string, string>;
  textStyle?: React.CSSProperties;
  overrideH?: number;
  overrideW?: number;
}) {
  if (isHeaderRow || !isFillableCellElement(element)) {
    return (
      <CellElementView
        element={element}
        sampleData={sampleData}
        textStyle={textStyle}
        overrideH={overrideH}
        overrideW={overrideW}
      />
    );
  }
  const dragged = withDragSize(element, overrideH, overrideW);
  return (
    <div className="pointer-events-none">
      <CellElementFill
        element={dragged}
        cellKey={cellKey}
        cellStyle={cellStyle}
        sampleData={sampleData}
      />
    </div>
  );
}

/**
 * The row one element occupies in a cell's stack.
 *
 * Two things, and both are about the stack rather than the element: the gap
 * below it, and the cell's alignment reaching the BOX rather than just the text
 * in it (see `cellItemStyle` — `text-align` does not move a block). The
 * read-only branch and the editor branch each drew this wrapper by hand, with
 * the same two rules spelled out twice; a cell element drawn two ways is the
 * seam this whole change closes, and it reached the wrapper as well as the
 * contents.
 */
export function CellStackItem({
  el,
  cellStyle,
  last,
  children,
}: {
  el: CellElement;
  cellStyle?: TableCellStyle;
  /** No gap under the last element — the cell's own padding closes the stack. */
  last: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={last ? '' : 'mb-1'} style={cellItemStyle(cellStyle, el)}>
      {children}
    </div>
  );
}
